import React, { useEffect, useMemo, useState } from "react";
import { Button, Table, message } from "antd";
import { AnimatePresence, motion } from "framer-motion";
import API from "../utils/api"; // adjust path if needed

export function FlightPlane(props) {
  return (
    <svg
      viewBox="0 0 58.568 58.568"
      width="1em"
      height="1em"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M1023.994,672.006c2.4-3.218,3.843-6.559,3.85-8.946a3.323,3.323,0,0,0-.453-1.828,1.706,1.706,0,0,0-.578-.584,3.265,3.265,0,0,0-1.852-.466c-2.381,0-5.723,1.442-8.942,3.849a77.166,77.166,0,0,0-8.806,8.487q-.917.966-1.934,2.031l-6.976-2.091.02-.02a1.566,1.566,0,0,0-.005-2.2l-1.259-1.261a1.571,1.571,0,0,0-1.11-.461,1.541,1.541,0,0,0-1.1.458l-1.885,1.886-1-.3a1.563,1.563,0,0,0-.125-2.063l-1.266-1.265a1.568,1.568,0,0,0-1.109-.457,1.534,1.534,0,0,0-1.1.458l-1.73,1.73-7.094-2.125a1.775,1.775,0,0,0-.507-.072,1.875,1.875,0,0,0-1.311.531l-2.494,2.494a1.463,1.463,0,0,0-.439,1.029,1.433,1.433,0,0,0,.761,1.284l14.918,8.334a2.607,2.607,0,0,1,.362.275l4.253,4.252c-3.978,4.191-7.522,8.25-10.284,11.781-.258.328-.5.649-.742.965l-11.541-1.048a1.573,1.573,0,0,0-.2-.012,2.127,2.127,0,0,0-1.429.591l-1.163,1.161a1.449,1.449,0,0,0-.446,1.029,1.418,1.418,0,0,0,.839,1.3l8.119,3.857a1.843,1.843,0,0,1,.311.223l.674.668a4.938,4.938,0,0,0-.325,1.561,2.053,2.053,0,0,0,.567,1.5l.029.026.008.005a2.056,2.056,0,0,0,1.493.56,4.964,4.964,0,0,0,1.548-.322l.684.682a1.956,1.956,0,0,1,.212.3l3.848,8.1a1.435,1.435,0,0,0,1.294.851h.008a1.446,1.446,0,0,0,1.026-.432l1.192-1.2a2.132,2.132,0,0,0,.569-1.426c0-.066,0-.117-.005-.144l-1.051-11.581c.317-.238.636-.484.965-.739,3.536-2.766,7.6-6.313,11.779-10.286l4.256,4.26a2.314,2.314,0,0,1,.278.365l8.318,14.891a1.451,1.451,0,0,0,2.322.342l2.512-2.51a1.9,1.9,0,0,0,.514-1.31,1.769,1.769,0,0,0-.069-.491l-2.125-7.095,1.729-1.733a1.563,1.563,0,0,0,0-2.205l-1.268-1.267a1.559,1.559,0,0,0-2.062-.129l-.3-.995,1.887-1.886a1.577,1.577,0,0,0,.455-1.107,1.54,1.54,0,0,0-.455-1.1l-1.262-1.262a1.563,1.563,0,0,0-2.217,0l-.013.012-2.089-6.976q1.082-1.032,2.062-1.96C1019.32,677.193,1022.053,674.6,1023.994,672.006Z"
        transform="translate(-969.276 -660.182)"
        fill="currentColor"
      />
    </svg>
  );
}

const money = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN");
};

const sheetVariants = {
  hidden: { y: "100%" },
  visible: {
    y: "0%",
    transition: { type: "spring", stiffness: 140, damping: 14, mass: 0.9 },
  },
  exit: {
    y: "100%",
    transition: { type: "tween", duration: 0.22, ease: "easeInOut" },
  },
};

const backdropVariants = {
  hidden: { opacity: 0, backdropFilter: "blur(0px)" },
  visible: {
    opacity: 1,
    backdropFilter: "blur(12px)",
    transition: { duration: 0.22, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    backdropFilter: "blur(0px)",
    transition: { duration: 0.18, ease: "easeIn" },
  },
};

const contentContainer = {
  hidden: {},
  visible: { transition: { delayChildren: 0.2, staggerChildren: 0.1 } },
  exit: { transition: { staggerChildren: 0.06, staggerDirection: -1 } },
};

const item = {
  hidden: { opacity: 0, y: 16, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.32, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: 10,
    filter: "blur(8px)",
    transition: { duration: 0.18, ease: "easeIn" },
  },
};

const blobA = {
  initial: { x: -40, y: 0, opacity: 0.55 },
  animate: { x: [-40, 30, -40], y: [0, -25, 0], opacity: [0.45, 0.7, 0.45] },
};
const blobB = {
  initial: { x: 30, y: 30, opacity: 0.35 },
  animate: { x: [30, -20, 30], y: [30, 5, 30], opacity: [0.25, 0.55, 0.25] },
};

const TarangTravelLeaguePopup = ({ open, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ Fetch from backend when popup opens
  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 100 };
      const res = await API.get("/manager/ttl/leaderboard", { params });
      setRows(res.data.leaderboard || []);
    } catch (err) {
      console.error("TTL fetch error:", err);
      message.error("Failed to fetch leaderboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original || "";
    };
  }, [open]);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const columns = useMemo(
    () => [
      {
        title: "Rank",
        dataIndex: "rank",
        key: "rank",
        render: (rank) => {
          let style = "";

          if (rank === 1) {
            style =
              "bg-yellow-100 text-yellow-700 font-bold px-3 py-1 rounded-full";
          } else if (rank === 2) {
            style =
              "bg-gray-200 text-gray-700 font-bold px-3 py-1 rounded-full";
          } else if (rank === 3) {
            style =
              "bg-orange-100 text-orange-700 font-bold px-3 py-1 rounded-full";
          } else {
            style = "text-gray-700 font-semibold";
          }

          return <span className={`text-lg ${style}`}>#{rank}</span>;
        },
      },
      {
        title: "Enrollment",
        dataIndex: "enrollment",
        key: "enrollment",
      },
      {
        title: "Manager",
        dataIndex: "manager",
        key: "manager",
      },
      // {
      //   title: "Join Date",
      //   dataIndex: "joinDate",
      //   key: "joinDate",
      //   render: (date) => (date ? new Date(date).toLocaleDateString() : "N/A"),
      // },
      {
        title: "Launch (IN)",
        dataIndex: "launchIn",
        key: "launchIn",
      },
      {
        title: "Sales (IN)",
        dataIndex: "salesIn",
        key: "salesIn",
        render: (value) =>
          typeof value === "number"
            ? value.toFixed(2)
            : Number(value || 0).toFixed(2),
      },
      {
        title: "Launch (AE)",
        dataIndex: "launchAe",
        key: "launchAe",
      },
      {
        title: "Sales (AE)",
        dataIndex: "salesAe",
        key: "salesAe",
        render: (value) =>
          typeof value === "number"
            ? value.toFixed(2)
            : Number(value || 0).toFixed(2),
      },
      {
        title: "Total Sales",
        dataIndex: "totalSale",
        key: "totalSale",
        render: (value) =>
          typeof value === "number"
            ? value.toFixed(2)
            : Number(value || 0).toFixed(2),
      },
      {
        title: "Prize",
        dataIndex: "prize",
        key: "prize",
        render: (val) => val || "—",
      },
    ],
    [],
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9999]"
          role="dialog"
          aria-modal="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.15 } }}
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
        >
          <motion.div
            className="absolute inset-0 bg-black/30"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            style={{ willChange: "backdrop-filter, opacity" }}
          />

          <motion.div
            variants={sheetVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="
              absolute inset-0
              md:inset-24 md:rounded-3xl md:shadow-2xl md:border md:border-black/10
              overflow-hidden
              bg-white
            "
            style={{ transformOrigin: "50% 100%" }}
          >
            <motion.div
              className="h-full w-full flex flex-col"
              variants={contentContainer}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Header */}
              <motion.div
                variants={item}
                className="
                  sticky top-0 z-10
                  bg-gradient-to-r from-[#0a1633] via-[#0b2a58] to-[#061127]
                  border-b border-white/10
                  px-4 py-3 sm:px-6 sm:py-4
                  flex items-start sm:items-center justify-between gap-3
                "
              >
                <div className="min-w-0">
                  <div className="relative flex items-center gap-2 min-w-0">
                    <AnimatePresence mode="wait">
                      {open && (
                        <motion.span
                          className="absolute text-cyan-200 drop-shadow-[0_0_18px_rgba(34,211,238,0.65)]"
                          style={{ fontSize: "44px", willChange: "transform" }}
                          initial={{ x: "-20%", opacity: 0 }}
                          animate={{
                            x: ["-20%", "2000%"],
                            opacity: [0, 1, 1, 0],
                          }}
                          transition={{
                            x: {
                              duration: 3,
                              repeat: Infinity,
                              ease: "linear",
                            },
                            opacity: {
                              duration: 3,
                              repeat: Infinity,
                              ease: "linear",
                            },
                          }}
                        >
                          <FlightPlane />
                        </motion.span>
                      )}
                    </AnimatePresence>

                    <h2 className="text-base sm:text-2xl font-extrabold tracking-tight text-white truncate">
                      TARANG TRAVEL LEAGUE
                    </h2>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-white/75">
                    <span>
                      For detailed information regarding rankings, prize
                      structure, and eligibility criteria, please contact your
                      respective Manager.
                    </span>

                    {/* <span className="text-white/60">|</span> */}

                    {/* <Button
                      size="small"
                      href="https://ttl.saumiccraft.in"
                      target="_blank"
                      className="
                        !bg-white 
                        !border 
                        !border-indigo-200
                        hover:!bg-indigo-50 
                        hover:!text-indigo-700
                        transition-colors 
                        duration-200 
                        rounded-full 
                        text-[#0a1633]
                        px-4 
                        font-medium
                        shadow-sm
                      "
                    >
                      Visit TTL Portal
                    </Button> */}
                  </div>
                </div>

                <motion.button
                  type="button"
                  onClick={onClose}
                  whileTap={{ scale: 0.98 }}
                  className="shrink-0 rounded-full px-4 py-2 text-sm font-semibold bg-white text-[#0a1633] hover:bg-white/90 transition"
                >
                  Close
                </motion.button>
              </motion.div>

              {/* Body */}
              <div className="relative flex-1 overflow-hidden bg-white">
                <motion.div
                  className="pointer-events-none absolute -left-10 top-10 h-52 w-52 rounded-full bg-cyan-300/35 blur-3xl"
                  variants={blobA}
                  initial="initial"
                  animate="animate"
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <motion.div
                  className="pointer-events-none absolute right-0 bottom-0 h-60 w-60 rounded-full bg-indigo-400/25 blur-3xl"
                  variants={blobB}
                  initial="initial"
                  animate="animate"
                  transition={{
                    duration: 7.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />

                <motion.div
                  variants={item}
                  className="relative h-full w-full p-3 bg-gradient-to-tr from-red-100 via-purple-100 to-blue-100"
                >
                  <div className="h-full w-full rounded-2xl border border-black/10 shadow-sm overflow-y-auto hide-scrollbar">
                    <Table
                      bordered
                      size="small"
                      dataSource={rows}
                      columns={columns}
                      rowKey="_id"
                      loading={loading}
                      pagination={false}
                      sticky
                      scroll={{ x: "max-content" }}
                      rowClassName={(record) => {
                        if (record.rank === 1) return "bg-yellow-50";
                        if (record.rank === 2) return "bg-gray-50";
                        if (record.rank === 3) return "bg-orange-50";
                        return "";
                      }}
                    />
                  </div>

                  <p className="mt-2 text-xs text-gray-500 sm:hidden">
                    Tip: swipe left/right to view all columns
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TarangTravelLeaguePopup;
